/**
 * 표(행렬)의 사각형 선택 영역을 **엑셀에 그대로 붙여넣을 수 있는 클립보드 데이터**로 만든다. (2026-09-28)
 * 사용처 = '설문조사 분석' 탭의 응답자별 응답 그리드(`SurveyResponseGrid.js`).
 *
 * 클립보드에는 두 형식을 함께 싣는다.
 *  - `text/plain` = TSV. 탭·줄바꿈·큰따옴표가 든 칸은 큰따옴표로 감싼다(엑셀·구글시트가 읽는 규칙).
 *  - `text/html`  = `<table>`. 엑셀은 이쪽을 우선 읽는다. 여기서만 **셀 서식을 '텍스트'로 고정**할 수 있다
 *    (`mso-number-format:"\@"`). TSV 만 주면 `01012345678` 의 앞 0 이 사라지고 `1-2` 같은 답이 날짜로 바뀐다.
 *
 * 좌표는 **행렬 전체 기준**이다(0행 = 헤더). 선택은 `{anchor, focus}` 두 점이고 방향은 상관없다.
 */

/** 두 점을 위·아래·왼쪽·오른쪽 경계로 바꾼다. 드래그 방향과 무관하게 같은 사각형이 나온다. */
export const normalizeRange = (anchor, focus) => ({
    top: Math.min(anchor.r, focus.r),
    bottom: Math.max(anchor.r, focus.r),
    left: Math.min(anchor.c, focus.c),
    right: Math.max(anchor.c, focus.c),
});

export const isInRange = (range, r, c) =>
    !!range && r >= range.top && r <= range.bottom && c >= range.left && c <= range.right;

/** 범위를 행렬 크기 안으로 자른다. 데이터가 다시 불려와 줄어든 뒤 남은 선택이 빈 칸을 복사하지 않게 한다. */
export const clampRange = (range, rowCount, colCount) => {
    if (!range || rowCount === 0 || colCount === 0) return null;
    const clamp = (v, max) => Math.max(0, Math.min(v, max - 1));
    return {
        top: clamp(range.top, rowCount),
        bottom: clamp(range.bottom, rowCount),
        left: clamp(range.left, colCount),
        right: clamp(range.right, colCount),
    };
};

export const sliceMatrix = (matrix, range) =>
    matrix.slice(range.top, range.bottom + 1).map(row => row.slice(range.left, range.right + 1));

const cellText = (value) => (value === null || value === undefined ? "" : String(value));

const escapeTsvCell = (value) => {
    const text = cellText(value);
    return /[\t\r\n"]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const toTsv = (matrix) => matrix.map(row => row.map(escapeTsvCell).join("\t")).join("\r\n");

const escapeHtml = (text) => text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * @param matrix      잘라낸 행렬
 * @param textColumns 각 열을 엑셀 '텍스트' 서식으로 붙일지 여부(잘라낸 열 기준). 없으면 전부 일반 서식.
 */
export const toClipboardHtml = (matrix, textColumns = []) => {
    const rows = matrix.map(row => {
        const cells = row.map((value, c) => {
            const style = textColumns[c] ? ` style="mso-number-format:'\\@'"` : "";
            // 한 칸 안의 줄바꿈은 같은 칸에 두라는 엑셀 전용 지시. 없으면 줄마다 행이 갈라진다.
            const html = escapeHtml(cellText(value)).replace(/\r\n|\r|\n/g, '<br style="mso-data-placement:same-cell">');
            return `<td${style}>${html}</td>`;
        });
        return `<tr>${cells.join("")}</tr>`;
    });
    return `<html><head><meta charset="utf-8"></head><body><table>${rows.join("")}</table></body></html>`;
};

/**
 * 클립보드에 TSV + HTML 을 함께 쓴다. **키 입력·클릭 핸들러 안에서 동기로** 불러야 한다.
 *
 * 숨긴 textarea 를 선택해 `execCommand("copy")` 를 부르고, 그때 뜨는 `copy` 이벤트에서 두 형식을 직접 채운다.
 * `navigator.clipboard.write` 를 1순위로 쓰지 않는 이유 = 보안 컨텍스트(https·localhost)에서만 동작하고,
 * Safari 는 선택 영역이 비어 있으면 `copy` 이벤트 자체를 보내지 않아 그리드처럼 텍스트 선택을 막은 화면에서는
 * 단축키가 무반응이 된다. textarea 를 선택해 두면 모든 브라우저에서 이벤트가 뜬다.
 *
 * @returns {boolean} 복사 성공 여부
 */
export const writeSheetToClipboard = (tsv, html) => {
    const onCopy = (e) => {
        e.clipboardData.setData("text/plain", tsv);
        e.clipboardData.setData("text/html", html);
        e.preventDefault();
    };
    const active = document.activeElement;
    const textarea = document.createElement("textarea");
    textarea.value = tsv;
    textarea.setAttribute("readonly", "");
    textarea.style.cssText = "position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;pointer-events:none;";
    document.body.appendChild(textarea);
    textarea.select();
    document.addEventListener("copy", onCopy);
    let copied = false;
    try {
        copied = document.execCommand("copy");
    } catch (e) {
        copied = false;
    } finally {
        document.removeEventListener("copy", onCopy);
        document.body.removeChild(textarea);
        // 그리드로 포커스를 돌려줘야 이어서 단축키(⌘A·⌘C·Esc)가 먹는다. 스크롤 위치는 건드리지 않는다.
        active?.focus?.({preventScroll: true});
    }
    if (!copied && navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(tsv).catch(() => {});
        return true;
    }
    return copied;
};
